import { useEffect, useMemo } from 'react'
import {
  type InfiniteData,
  type QueryClient,
  useInfiniteQuery,
  useIsMutating,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type QueryObserverResult,
} from '@tanstack/react-query'
import { dealLeadsKeys } from '@/hooks/query-keys'
import * as dealLeadsService from '@/services/deal-leads.service'
import type { DealLeadView } from '@/features/deal-review/deal-lead-views'
import type {
  DealLeadDetail,
  DealLeadListItem,
  DealLeadSearch,
  CorrectDealLead,
  Filter,
  RecordDealDecision,
  SaveDealLeadNote,
  ArchiveDealLead,
  SendToDealCloud,
  Sort,
} from '@/types/api'

export { dealLeadsKeys } from '@/hooks/query-keys'

export const DEAL_LEADS_PAGE_SIZE = 25

const FAILING_POLL_MS = 60_000

// Back off after an error rather than stop: focus refetch is off, so a stopped poll never resumes.
function pollEvery(state: { error: unknown }, ms: number) {
  return state.error ? Math.max(ms, FAILING_POLL_MS) : ms
}

// One write at a time per lead, so a second edit waits for the first's version.
function leadScope(leadId: string | undefined) {
  return { id: `deal-lead:${leadId}` }
}

// Read at send time: an earlier write in the same scope may have moved the version.
function latestVersion(
  queryClient: QueryClient,
  leadId: string,
  version: number
) {
  const cached = queryClient.getQueryData<DealLeadDetail>(
    dealLeadsKeys.detail(leadId)
  )
  return Math.max(cached?.version ?? version, version)
}

function requireLeadId(leadId: string | undefined): string {
  if (leadId === undefined) throw new Error('A lead id is required')
  return leadId
}

export interface UseDealLeadsSearchOptions {
  view?: DealLeadView
  filters?: Filter[]
  sorts?: Sort[]
  enabled?: boolean

  live?: boolean
}

export function useDealLeadsSearch({
  view,
  filters,
  sorts,
  enabled = true,
  live = false,
}: UseDealLeadsSearchOptions = {}) {
  const params: Omit<DealLeadSearch, 'cursor'> = useMemo(
    () => ({
      limit: DEAL_LEADS_PAGE_SIZE,
      ...(view ? { view } : {}),
      ...(filters?.length ? { filters } : {}),
      ...(sorts?.length ? { sorts } : {}),
    }),
    [view, filters, sorts]
  )

  const query = useInfiniteQuery({
    queryKey: dealLeadsKeys.list(params),
    queryFn: ({ pageParam }) =>
      dealLeadsService.searchDealLeads({
        ...params,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasMore || !lastPage.nextCursor) return undefined
      // nextCursor is typed as an empty object but is a string at runtime.
      const cursor = lastPage.nextCursor
      return typeof cursor === 'string' ? cursor : undefined
    },
    enabled,
    // A new view drops the placeholder rows, since they belong to the other tab.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2].view === params.view ? previous : undefined,
    refetchInterval: (query) => (live ? pollEvery(query.state, 5_000) : false),
  })

  const leads: DealLeadListItem[] = useMemo(
    () => query.data?.pages.flatMap((page) => page.data) ?? [],
    [query.data]
  )

  return { ...query, leads }
}

export function useDealLeadCounts() {
  return useQuery({
    queryKey: dealLeadsKeys.counts(),
    queryFn: dealLeadsService.getDealLeadCounts,
    refetchInterval: (query) => pollEvery(query.state, 15_000),
  })
}

export function useIntakePollStatus() {
  return useQuery({
    queryKey: dealLeadsKeys.pollStatus(),
    queryFn: dealLeadsService.getIntakePollStatus,
    refetchInterval: (query) => pollEvery(query.state, 60_000),
  })
}

export function usePollIntakeNow() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: dealLeadsService.pollIntakeNow,
    // On settled: a timed-out check may still have captured mail.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: dealLeadsKeys.all })
    },
  })
}

export function useIntakeActivity(enabled: boolean) {
  return useQuery({
    queryKey: dealLeadsKeys.intakeActivity(),
    queryFn: dealLeadsService.getIntakeActivity,
    enabled,
    refetchInterval: (query) =>
      enabled ? pollEvery(query.state, 10_000) : false,
  })
}

export function useIntakeIssues(enabled: boolean) {
  return useQuery({
    queryKey: dealLeadsKeys.intakeIssues(),
    queryFn: dealLeadsService.searchIntakeIssues,
    enabled,
  })
}

export function useRetryIntakeMessage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: dealLeadsService.retryIntakeMessage,
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: dealLeadsKeys.all })
    },
  })
}

export function useRescreenDealLead(id: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: dealLeadsKeys.rescreen(id),
    mutationFn: dealLeadsService.rescreenDealLead,
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: dealLeadsKeys.all })
    },
  })
}

// Here rather than in the panel: its suites mock this module wholesale.
export function useIsRescreening(id: string | undefined): boolean {
  return useIsMutating({ mutationKey: dealLeadsKeys.rescreen(id ?? '') }) > 0
}

export function useRereadDealLead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      discardCorrections,
    }: {
      id: string
      discardCorrections: boolean
    }) => dealLeadsService.rereadDealLead(id, discardCorrections),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: dealLeadsKeys.all })
    },
  })
}

export function usePrefetchAdjacentLeads(ids: (string | null | undefined)[]) {
  const queryClient = useQueryClient()
  const key = ids.filter(Boolean).join('|')

  useEffect(() => {
    for (const id of key.split('|').filter(Boolean)) {
      void queryClient.prefetchQuery({
        queryKey: dealLeadsKeys.detail(id),
        queryFn: () => dealLeadsService.getDealLead(id),
      })
    }
  }, [key, queryClient])
}

function detailsOf(results: QueryObserverResult<DealLeadDetail>[]) {
  return results.map((result) => result.data)
}

// Never fetched here; every write lands its response in this cache.
export function useCachedDealLeads(ids: string[]) {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: dealLeadsKeys.detail(id),
      queryFn: () => dealLeadsService.getDealLead(id),
      enabled: false,
    })),
    combine: detailsOf,
  })
}

export function useDealLead(id: string | undefined) {
  return useQuery({
    queryKey: dealLeadsKeys.detail(id ?? ''),
    queryFn: () => dealLeadsService.getDealLead(requireLeadId(id)),
    enabled: Boolean(id),
    refetchInterval: (query) => {
      const lead = query.state.data
      if (!lead) return false
      const running =
        lead.processingState === 'QUEUED' ||
        lead.processingState === 'PROCESSING'
      return running ? pollEvery(query.state, 3_000) : false
    },
  })
}

export function useDealLeadFieldOptions() {
  return useQuery({
    queryKey: dealLeadsKeys.fieldOptions(),
    queryFn: dealLeadsService.getDealLeadFieldOptions,
    staleTime: Infinity,
  })
}

export function useDealDecisionOptions() {
  return useQuery({
    queryKey: dealLeadsKeys.decisionOptions(),
    queryFn: dealLeadsService.getDealDecisionOptions,
    staleTime: Infinity,
  })
}

// Response written to the detail cache so the next save carries the new version.
export function useRecordDealDecision(leadId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    scope: leadScope(leadId),
    mutationFn: (body: RecordDealDecision) => {
      const id = requireLeadId(leadId)
      return dealLeadsService.recordDealDecision(id, {
        ...body,
        version: latestVersion(queryClient, id, body.version),
      })
    },
    onSuccess: (detail: DealLeadDetail) => {
      queryClient.setQueryData(dealLeadsKeys.detail(detail.id), detail)
      patchListedLead(queryClient, detail.id, {
        disposition: detail.disposition,
        dispositionReason: detail.dispositionReason,
        addOnConfirmed: detail.addOnConfirmed,
        version: detail.version,
      })
    },
    // A 404 or 409 also means the row on the board is out of date.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: dealLeadsKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: dealLeadsKeys.counts() })
    },
  })
}

export function useSaveDealLeadNote(leadId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    scope: leadScope(leadId),
    mutationFn: (body: SaveDealLeadNote) => {
      const id = requireLeadId(leadId)
      return dealLeadsService.saveDealLeadNote(id, {
        ...body,
        version: latestVersion(queryClient, id, body.version),
      })
    },
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: dealLeadsKeys.lists() })
      const previous = queryClient.getQueriesData<DealLeadPages>({
        queryKey: dealLeadsKeys.lists(),
      })
      const detailKey = dealLeadsKeys.detail(leadId ?? '')
      const previousDetail = queryClient.getQueryData<DealLeadDetail>(detailKey)
      const commentary = body.commentary ?? undefined
      patchListedLead(queryClient, leadId, {
        dispositionCommentary: commentary,
      })
      // A row held in the Inbox after its decision reads the detail, not a list.
      if (previousDetail) {
        queryClient.setQueryData(detailKey, {
          ...previousDetail,
          dispositionCommentary: commentary,
        })
      }
      return { previous, previousDetail }
    },
    onError: (_error, _body, context) => {
      for (const [key, data] of context?.previous ?? []) {
        queryClient.setQueryData(key, data)
      }
      if (context?.previousDetail) {
        queryClient.setQueryData(
          dealLeadsKeys.detail(context.previousDetail.id),
          context.previousDetail
        )
      }
    },
    onSuccess: (detail: DealLeadDetail) => {
      queryClient.setQueryData(dealLeadsKeys.detail(detail.id), detail)
      patchListedLead(queryClient, detail.id, { version: detail.version })
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: dealLeadsKeys.lists() })
    },
  })
}

type DealLeadPages = InfiniteData<
  Awaited<ReturnType<typeof dealLeadsService.searchDealLeads>>
>

function patchListedLead(
  queryClient: QueryClient,
  id: string | undefined,
  patch: Partial<DealLeadListItem>
) {
  queryClient.setQueriesData<DealLeadPages>(
    { queryKey: dealLeadsKeys.lists() },
    (data) =>
      data && {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          data: page.data.map((lead) =>
            lead.id === id ? { ...lead, ...patch } : lead
          ),
        })),
      }
  )
}

export function useSetDealLeadArchived(leadId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    scope: leadScope(leadId),
    mutationFn: (body: ArchiveDealLead) => {
      const id = requireLeadId(leadId)
      return dealLeadsService.setDealLeadArchived(id, {
        ...body,
        version: latestVersion(queryClient, id, body.version),
      })
    },
    onSuccess: (detail: DealLeadDetail) => {
      queryClient.setQueryData(dealLeadsKeys.detail(detail.id), detail)
      void queryClient.invalidateQueries({ queryKey: dealLeadsKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: dealLeadsKeys.counts() })
    },
  })
}

export function useCorrectDealLead(leadId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    scope: leadScope(leadId),
    mutationFn: (body: CorrectDealLead) => {
      const id = requireLeadId(leadId)
      return dealLeadsService.correctDealLead(id, {
        ...body,
        version: latestVersion(queryClient, id, body.version),
      })
    },
    onSuccess: (detail: DealLeadDetail) => {
      queryClient.setQueryData(dealLeadsKeys.detail(detail.id), detail)
      void queryClient.invalidateQueries({ queryKey: dealLeadsKeys.lists() })
    },
  })
}

export interface DealLeadName {
  lead: DealLeadListItem
  name: string
}

// Returns how many names were not saved.
export function useNameDealLeads() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (names: DealLeadName[]): Promise<number> => {
      const results = await Promise.allSettled(
        names.map(({ lead, name }) =>
          dealLeadsService.correctDealLead(lead.id, {
            version: lead.version,
            companyName: name,
          })
        )
      )
      for (const result of results) {
        if (result.status === 'fulfilled') {
          queryClient.setQueryData(
            dealLeadsKeys.detail(result.value.id),
            result.value
          )
        }
      }
      await queryClient.invalidateQueries({ queryKey: dealLeadsKeys.lists() })
      return results.filter((result) => result.status === 'rejected').length
    },
  })
}

export function useSendToDealCloud(leadId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    scope: leadScope(leadId),
    mutationFn: (body: SendToDealCloud) => {
      const id = requireLeadId(leadId)
      return dealLeadsService.sendDealLeadToDealCloud(id, {
        ...body,
        version: latestVersion(queryClient, id, body.version),
      })
    },
    onSuccess: (detail: DealLeadDetail) => {
      queryClient.setQueryData(dealLeadsKeys.detail(detail.id), detail)
    },
    // A 404 or 409 also means the row on the board is out of date.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: dealLeadsKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: dealLeadsKeys.counts() })
    },
  })
}
